import { getConfig } from '../shared/config/runtime-env'

// Development-only defaults (used when import.meta.env.DEV is true)
const GW = 'https://localhost:8443'
const DEV_DEFAULTS = {
  VITE_API_URL: GW,
  VITE_AUTH_SERVICE_URL: GW,
  VITE_UNIFIED_GATEWAY_URL: GW,
  VITE_EXECUTION_ZONE_URL: `${GW}/v1/agents`,
  VITE_COORDINATION_ZONE_URL: 'wss://localhost:8443/ws',
  VITE_GOVERNANCE_ZONE_URL: GW,
  VITE_ORY_KETO_READ_URL: `${GW}/authz/read`,
  VITE_ORY_KETO_WRITE_URL: `${GW}/authz/write`,
  VITE_REGISTRY_SERVICE_URL: GW,
  VITE_MISSION_CONTROL_URL: `${GW}/v1/agents`,
  VITE_AGENT_RUNTIME_URL: `${GW}/v1/agents`,
  VITE_GOVERNANCE_SERVICE_URL: GW,
  VITE_DATA_ZONE_URL: `${GW}/v1/data`,
  VITE_GRAFANA_URL: 'http://localhost:3000',
  VITE_LOKI_URL: `${GW}/logs`,
  VITE_TEMPO_URL: `${GW}/traces`,
  VITE_MIMIR_URL: `${GW}/metrics`,
} as const

// API Configuration
export const API_CONFIG = {
  // Base URL for the backend services
  // Development: localhost (Vite proxy forwards to backend)
  // Production: Injected at container startup via window.__RUNTIME_CONFIG__ or build-time env
  // Runtime: injected at container startup via window.__RUNTIME_CONFIG__
  baseURL: getConfig('VITE_API_URL', DEV_DEFAULTS.VITE_API_URL),

  // Service endpoints
  // Development defaults to localhost
  // Production: MUST be configured via runtime config or build-time environment variables
  services: {
    // Unified Gateway - main API gateway
    unifiedGateway: getConfig('VITE_UNIFIED_GATEWAY_URL', DEV_DEFAULTS.VITE_UNIFIED_GATEWAY_URL),

    // Platform zones
    executionZone: getConfig('VITE_EXECUTION_ZONE_URL', DEV_DEFAULTS.VITE_EXECUTION_ZONE_URL),
    coordinationZone: getConfig('VITE_COORDINATION_ZONE_URL', DEV_DEFAULTS.VITE_COORDINATION_ZONE_URL),
    governanceZone: getConfig('VITE_GOVERNANCE_ZONE_URL', DEV_DEFAULTS.VITE_GOVERNANCE_ZONE_URL),

    // Authentication/Authorization
    // Runtime: injected at container startup via window.__RUNTIME_CONFIG__
    auth: getConfig('VITE_AUTH_SERVICE_URL', DEV_DEFAULTS.VITE_AUTH_SERVICE_URL),

    // Authorization service (Ory Keto)
    oryKeto: {
      readUrl: getConfig('VITE_ORY_KETO_READ_URL', DEV_DEFAULTS.VITE_ORY_KETO_READ_URL),
      writeUrl: getConfig('VITE_ORY_KETO_WRITE_URL', DEV_DEFAULTS.VITE_ORY_KETO_WRITE_URL),
    },

    // Legacy aliases for backward compatibility
    registry: getConfig('VITE_REGISTRY_SERVICE_URL', DEV_DEFAULTS.VITE_REGISTRY_SERVICE_URL),
    missionControl: getConfig('VITE_MISSION_CONTROL_URL', DEV_DEFAULTS.VITE_MISSION_CONTROL_URL),
    agentRuntime: getConfig('VITE_AGENT_RUNTIME_URL', DEV_DEFAULTS.VITE_AGENT_RUNTIME_URL),
    authorization: getConfig('VITE_AUTH_SERVICE_URL', DEV_DEFAULTS.VITE_AUTH_SERVICE_URL),
    governance: getConfig('VITE_GOVERNANCE_SERVICE_URL', DEV_DEFAULTS.VITE_GOVERNANCE_SERVICE_URL),

    // Data Zone
    dataZone: getConfig('VITE_DATA_ZONE_URL', DEV_DEFAULTS.VITE_DATA_ZONE_URL),

    // Observability stack (LGTM)
    // Runtime: injected at container startup via window.__RUNTIME_CONFIG__
    grafana: getConfig('VITE_GRAFANA_URL', DEV_DEFAULTS.VITE_GRAFANA_URL),
    loki: getConfig('VITE_LOKI_URL', DEV_DEFAULTS.VITE_LOKI_URL),
    tempo: getConfig('VITE_TEMPO_URL', DEV_DEFAULTS.VITE_TEMPO_URL),
    mimir: getConfig('VITE_MIMIR_URL', DEV_DEFAULTS.VITE_MIMIR_URL),
  },

  // Request timeout in milliseconds
  timeout: 30000,

  // Retry configuration
  retry: {
    maxAttempts: 3,
    backoffMultiplier: 2,
    initialDelay: 1000,
  },

  // Default headers
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },

  // Environment detection
  isDevelopment: () => import.meta.env.DEV || window.location.hostname === 'localhost',
  isProduction: () => import.meta.env.PROD && window.location.hostname !== 'localhost',
}

// Helper function to build full URL
export function buildApiUrl(path: string, service: keyof typeof API_CONFIG.services = 'registry'): string {
  const serviceConfig = API_CONFIG.services[service]
  const baseUrl = typeof serviceConfig === 'string' ? serviceConfig : (typeof serviceConfig === 'object' && 'readUrl' in serviceConfig ? serviceConfig.readUrl : API_CONFIG.baseURL)

  if (!baseUrl) {
    throw new Error(
      `CONFIGURATION ERROR: Service URL for '${service}' is not configured. ` +
      `Please set the appropriate environment variable in your Kubernetes ConfigMap.`
    )
  }

  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`
}

// Helper function to get Ory Keto URL
export function getOryKetoUrl(endpoint: 'read' | 'write' = 'read'): string {
  const ketoConfig = API_CONFIG.services.oryKeto
  const url = endpoint === 'write' ? ketoConfig.writeUrl : ketoConfig.readUrl

  if (!url) {
    throw new Error(
      `CONFIGURATION ERROR: Ory Keto ${endpoint} URL is not configured. ` +
      `Please set VITE_ORY_KETO_${endpoint.toUpperCase()}_URL in your Kubernetes ConfigMap.`
    )
  }

  return url
}

// Helper function to handle API errors
export async function handleApiError(response: Response): Promise<never> {
  let errorMessage = `API Error: ${response.status} ${response.statusText}`

  try {
    const errorData = await response.json()
    if (errorData.error) {
      errorMessage = errorData.error
    } else if (errorData.message) {
      errorMessage = errorData.message
    }
  } catch {
    // If parsing JSON fails, use the default error message
  }

  throw new Error(errorMessage)
}

// Helper function for making API requests with retry logic
export async function apiRequest<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const { maxAttempts, backoffMultiplier, initialDelay } = API_CONFIG.retry

  let lastError: Error | null = null

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...API_CONFIG.headers,
          ...options.headers,
        },
      })

      if (!response.ok) {
        await handleApiError(response)
      }

      return await response.json()
    } catch (error) {
      lastError = error as Error

      // Don't retry on client errors (4xx)
      if (error instanceof Error && error.message.includes('4')) {
        throw error
      }

      // Calculate delay for next retry
      if (attempt < maxAttempts - 1) {
        const delay = initialDelay * Math.pow(backoffMultiplier, attempt)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('API request failed after retries')
}
